// Fillable field maps for the Care Pro Transfer onboarding-application system.
// Same coordinate convention as documentTemplates.js: each field's `yTop` is
// the bottom edge (yMax) of the blank/label in top-down PDF-page coordinates,
// exactly as `pdftotext -bbox` reports it — pdfFill.js converts to pdf-lib's
// bottom-up space at render time.
//
// Field ownership is the *inverse* default of documentTemplates.js: most of
// an onboarding application is the applicant's to fill in, so fields are
// applicant-editable unless explicitly flagged `staffOnly: true` (a company
// signature line, hire date, etc.). The Employment Application below has no
// staff-only fields at all — the applicant fills and signs the whole thing.

const BASE_PATH = '/document-templates/carepro-onboarding';

export const CARE_PRO_TEMPLATES = [
  {
    key: 'employment_application',
    title: 'Care Pro Employment Application',
    sortOrder: 1,
    file: `${BASE_PATH}/Employment Application for Care Pros 08092026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'today_date', label: 'Today\'s date', type: 'date', page: 0, x: 152, yTop: 325.7, size: 9, maxWidth: 148, height: 12 },
      { key: 'position_applied_for', label: 'Position applied for', type: 'text', required: true, page: 0, x: 399.1, yTop: 325.7, size: 9, maxWidth: 139, height: 12 },
      { key: 'full_legal_name', label: 'Full legal name', type: 'text', required: true, page: 0, x: 152, yTop: 343.5, size: 9, maxWidth: 148, height: 12 },
      { key: 'preferred_name', label: 'Preferred name', type: 'text', page: 0, x: 398.8, yTop: 343.5, size: 9, maxWidth: 140, height: 12 },
      { key: 'street_address', label: 'Street address', type: 'text', required: true, page: 0, x: 152, yTop: 361.22, size: 9, maxWidth: 148, height: 12 },
      { key: 'city_state_zip', label: 'City / State / ZIP', type: 'text', required: true, page: 0, x: 397.5, yTop: 361.2, size: 9, maxWidth: 140, height: 12 },
      { key: 'mobile_phone', label: 'Mobile phone', type: 'phone', required: true, page: 0, x: 152, yTop: 378.98, size: 9, maxWidth: 148, height: 12 },
      { key: 'email', label: 'Email', type: 'email', required: true, page: 0, x: 398.1, yTop: 379.5, size: 9, maxWidth: 140, height: 12 },
      { key: 'previously_applied', label: 'Previously applied here?', type: 'radio', page: 0, options: [
        { value: 'no', label: 'No', x: 347.76, yTop: 432.38 },
        { value: 'yes', label: 'Yes', x: 375.41, yTop: 432.38 },
      ]},
      { key: 'previously_applied_when', label: 'When (if previously applied)', type: 'text', page: 0, x: 438, yTop: 432.38, size: 8, maxWidth: 95, height: 12 },
      { key: 'previously_employed', label: 'Previously employed here?', type: 'radio', page: 0, options: [
        { value: 'no', label: 'No', x: 347.76, yTop: 450.14 },
        { value: 'yes', label: 'Yes', x: 375.41, yTop: 450.14 },
      ]},
      { key: 'previously_employed_when', label: 'When (if previously employed)', type: 'text', page: 0, x: 438, yTop: 450.14, size: 8, maxWidth: 95, height: 12 },
      { key: 'able_to_perform', label: 'Able to perform essential job functions with or without reasonable accommodation?', type: 'radio', page: 0, options: [
        { value: 'yes', label: 'Yes', x: 347.76, yTop: 471.02 },
        { value: 'no', label: 'No', x: 377.35, yTop: 471.02 },
      ]},
      { key: 'how_did_you_hear', label: 'How did you hear about us?', type: 'text', page: 0, x: 345, yTop: 492.4, size: 9, maxWidth: 192.5, height: 12 },
      { key: 'available_start_date', label: 'Available start date', type: 'date', required: true, page: 1, x: 165, yTop: 86.8, size: 9, maxWidth: 139, height: 12 },
      { key: 'desired_hours_per_week', label: 'Desired hours per week', type: 'text', required: true, page: 1, x: 419, yTop: 86.8, size: 9, maxWidth: 118.5, height: 12 },
      { key: 'available_from_mon', label: 'Available from — Monday', type: 'time', page: 1, x: 175, yTop: 136.22, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_mon', label: 'Available until — Monday', type: 'time', page: 1, x: 300, yTop: 136.22, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_mon', label: 'Not available — Monday', type: 'checkbox', page: 1, x: 419.76, yTop: 136.22 },
      { key: 'available_from_tue', label: 'Available from — Tuesday', type: 'time', page: 1, x: 175, yTop: 153.98, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_tue', label: 'Available until — Tuesday', type: 'time', page: 1, x: 300, yTop: 153.98, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_tue', label: 'Not available — Tuesday', type: 'checkbox', page: 1, x: 419.76, yTop: 153.98 },
      { key: 'available_from_wed', label: 'Available from — Wednesday', type: 'time', page: 1, x: 175, yTop: 171.74, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_wed', label: 'Available until — Wednesday', type: 'time', page: 1, x: 300, yTop: 171.74, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_wed', label: 'Not available — Wednesday', type: 'checkbox', page: 1, x: 419.76, yTop: 171.74 },
      { key: 'available_from_thu', label: 'Available from — Thursday', type: 'time', page: 1, x: 175, yTop: 189.5, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_thu', label: 'Available until — Thursday', type: 'time', page: 1, x: 300, yTop: 189.5, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_thu', label: 'Not available — Thursday', type: 'checkbox', page: 1, x: 419.76, yTop: 189.5 },
      { key: 'available_from_fri', label: 'Available from — Friday', type: 'time', page: 1, x: 175, yTop: 207.26, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_fri', label: 'Available until — Friday', type: 'time', page: 1, x: 300, yTop: 207.26, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_fri', label: 'Not available — Friday', type: 'checkbox', page: 1, x: 419.76, yTop: 207.26 },
      { key: 'available_from_sat', label: 'Available from — Saturday', type: 'time', page: 1, x: 175, yTop: 224.9, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_sat', label: 'Available until — Saturday', type: 'time', page: 1, x: 300, yTop: 224.9, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_sat', label: 'Not available — Saturday', type: 'checkbox', page: 1, x: 419.76, yTop: 224.9 },
      { key: 'available_from_sun', label: 'Available from — Sunday', type: 'time', page: 1, x: 175, yTop: 242.66, size: 9, maxWidth: 110, height: 12 },
      { key: 'available_until_sun', label: 'Available until — Sunday', type: 'time', page: 1, x: 300, yTop: 242.66, size: 9, maxWidth: 110, height: 12 },
      { key: 'not_available_sun', label: 'Not available — Sunday', type: 'checkbox', page: 1, x: 419.76, yTop: 242.66 },
      { key: 'relevant_experience', label: 'Describe relevant caregiving, employment, volunteer, training, certification, or life experience', type: 'text', required: true, page: 1, x: 75, yTop: 333.5, size: 9, maxWidth: 460, height: 26.8 },
      { key: 'interests_older_adults', label: 'What interests you about supporting older adults?', type: 'text', required: true, page: 1, x: 76.1, yTop: 402, size: 9, maxWidth: 460, height: 25.9 },
      { key: 'languages_skills', label: 'Languages, licenses, certifications, or other job-related skills', type: 'text', required: true, page: 1, x: 77, yTop: 457.5, size: 9, maxWidth: 460, height: 17.7 },
      { key: 'highest_level_completed', label: 'Highest level completed', type: 'radio', page: 1, options: [
        { value: 'less_than_hs', label: 'Less than high school', x: 217.8, yTop: 517.34 },
        { value: 'hs_ged', label: 'High school/GED', x: 316.24, yTop: 517.34 },
        { value: 'some_college', label: 'Some college', x: 397.4, yTop: 517.34 },
        { value: 'degree', label: 'Degree', x: 464.87, yTop: 517.34 },
      ]},
      { key: 'school_program', label: 'School / program', type: 'text', page: 1, x: 218.5, yTop: 534.5, size: 9, maxWidth: 300, height: 12 },
      { key: 'education_city_state', label: 'City / State (education)', type: 'text', page: 1, x: 218.5, yTop: 552.7, size: 9, maxWidth: 300, height: 12 },
      { key: 'degree_credential_field', label: 'Degree, credential, or field of study', type: 'text', page: 1, x: 217, yTop: 574.9, size: 9, maxWidth: 301.3, height: 12 },
      { key: 'employer_name_1', label: 'Employer 1 name', type: 'text', page: 2, x: 149.5, yTop: 137.3, size: 9, maxWidth: 150.5, height: 12 },
      { key: 'employer_city_state_1', label: 'Employer 1 city/state', type: 'text', page: 2, x: 384, yTop: 137.3, size: 9, maxWidth: 152, height: 12 },
      { key: 'employer_phone_1', label: 'Employer 1 phone', type: 'phone', page: 2, x: 149.5, yTop: 154.6, size: 9, maxWidth: 150, height: 12 },
      { key: 'employer_supervisor_1', label: 'Employer 1 supervisor', type: 'text', page: 2, x: 384, yTop: 154.6, size: 9, maxWidth: 135, height: 12 },
      { key: 'employer_dates_from_1', label: 'Employer 1 dates employed — from', type: 'text', page: 2, x: 177, yTop: 173.3, size: 8, maxWidth: 32.5, height: 12 },
      { key: 'employer_dates_to_1', label: 'Employer 1 dates employed — to', type: 'text', page: 2, x: 226.5, yTop: 173.3, size: 8, maxWidth: 72.5, height: 12 },
      { key: 'employer_job_title_1', label: 'Employer 1 job title', type: 'text', page: 2, x: 384, yTop: 172.34, size: 9, maxWidth: 135, height: 12 },
      { key: 'employer_contact_ok_1', label: 'Employer 1: May we contact?', type: 'radio', page: 2, options: [
        { value: 'yes', label: 'Yes', x: 152.76, yTop: 193.22 },
        { value: 'no', label: 'No', x: 182.35, yTop: 193.22 },
      ]},
      { key: 'employer_currently_employed_1', label: 'Employer 1: Currently employed?', type: 'radio', page: 2, options: [
        { value: 'yes', label: 'Yes', x: 386.76, yTop: 193.22 },
        { value: 'no', label: 'No', x: 416.35, yTop: 193.22 },
      ]},
      { key: 'employer_duties_1', label: 'Employer 1: Main duties and responsibilities', type: 'text', page: 2, x: 75.1, yTop: 255.6, size: 9, maxWidth: 460, height: 27.2 },
      { key: 'employer_reason_leaving_1', label: 'Employer 1: Reason for leaving', type: 'text', page: 2, x: 77, yTop: 312, size: 9, maxWidth: 460, height: 18.1 },
      { key: 'employer_name_2', label: 'Employer 2 name', type: 'text', page: 2, x: 150, yTop: 372.2, size: 9, maxWidth: 150.5, height: 12 },
      { key: 'employer_city_state_2', label: 'Employer 2 city/state', type: 'text', page: 2, x: 383.5, yTop: 371.7, size: 9, maxWidth: 150.5, height: 12 },
      { key: 'employer_phone_2', label: 'Employer 2 phone', type: 'phone', page: 2, x: 150, yTop: 389.4, size: 9, maxWidth: 151, height: 12 },
      { key: 'employer_supervisor_2', label: 'Employer 2 supervisor', type: 'text', page: 2, x: 383, yTop: 389.4, size: 9, maxWidth: 152.5, height: 12 },
      { key: 'employer_dates_from_2', label: 'Employer 2 dates employed — from', type: 'text', page: 2, x: 180, yTop: 407.18, size: 8, maxWidth: 28, height: 12 },
      { key: 'employer_dates_to_2', label: 'Employer 2 dates employed — to', type: 'text', page: 2, x: 228, yTop: 407.18, size: 8, maxWidth: 60, height: 12 },
      { key: 'employer_job_title_2', label: 'Employer 2 job title', type: 'text', page: 2, x: 383.5, yTop: 407.2, size: 9, maxWidth: 152.5, height: 12 },
      { key: 'employer_contact_ok_2', label: 'Employer 2: May we contact?', type: 'radio', page: 2, options: [
        { value: 'yes', label: 'Yes', x: 152.76, yTop: 427.94 },
        { value: 'no', label: 'No', x: 182.35, yTop: 427.94 },
      ]},
      { key: 'employer_currently_employed_2', label: 'Employer 2: Currently employed?', type: 'radio', page: 2, options: [
        { value: 'yes', label: 'Yes', x: 386.76, yTop: 427.94 },
        { value: 'no', label: 'No', x: 416.35, yTop: 427.94 },
      ]},
      { key: 'employer_duties_2', label: 'Employer 2: Main duties and responsibilities', type: 'text', page: 2, x: 76, yTop: 489.5, size: 9, maxWidth: 460, height: 25.5 },
      { key: 'employer_reason_leaving_2', label: 'Employer 2: Reason for leaving', type: 'text', page: 2, x: 77, yTop: 547, size: 9, maxWidth: 460, height: 17.7 },
      { key: 'employer_name_3', label: 'Employer 3 name', type: 'text', page: 3, x: 149.5, yTop: 111.5, size: 9, maxWidth: 151.5, height: 12 },
      { key: 'employer_city_state_3', label: 'Employer 3 city/state', type: 'text', page: 3, x: 384, yTop: 111.5, size: 9, maxWidth: 153.7, height: 12 },
      { key: 'employer_phone_3', label: 'Employer 3 phone', type: 'phone', page: 3, x: 149.5, yTop: 128.8, size: 9, maxWidth: 152, height: 12 },
      { key: 'employer_supervisor_3', label: 'Employer 3 supervisor', type: 'text', page: 3, x: 384, yTop: 128.8, size: 9, maxWidth: 153.3, height: 12 },
      { key: 'employer_dates_from_3', label: 'Employer 3 dates employed — from', type: 'text', page: 3, x: 176.5, yTop: 147, size: 8, maxWidth: 33.2, height: 12 },
      { key: 'employer_dates_to_3', label: 'Employer 3 dates employed — to', type: 'text', page: 3, x: 228, yTop: 146.5, size: 8, maxWidth: 60, height: 12 },
      { key: 'employer_job_title_3', label: 'Employer 3 job title', type: 'text', page: 3, x: 384.1, yTop: 147, size: 9, maxWidth: 153.7, height: 12 },
      { key: 'employer_contact_ok_3', label: 'Employer 3: May we contact?', type: 'radio', page: 3, options: [
        { value: 'yes', label: 'Yes', x: 152.76, yTop: 167.42 },
        { value: 'no', label: 'No', x: 182.35, yTop: 167.42 },
      ]},
      { key: 'employer_currently_employed_3', label: 'Employer 3: Currently employed?', type: 'radio', page: 3, options: [
        { value: 'yes', label: 'Yes', x: 386.76, yTop: 167.42 },
        { value: 'no', label: 'No', x: 416.35, yTop: 167.42 },
      ]},
      { key: 'employer_duties_3', label: 'Employer 3: Main duties and responsibilities', type: 'text', page: 3, x: 76, yTop: 229.5, size: 9, maxWidth: 460, height: 26.3 },
      { key: 'employer_reason_leaving_3', label: 'Employer 3: Reason for leaving', type: 'text', page: 3, x: 77, yTop: 287, size: 9, maxWidth: 460, height: 18.5 },
      { key: 'ref1_full_name', label: 'Reference 1: Full name', type: 'text', required: true, page: 3, x: 204.5, yTop: 371.7, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref1_phone', label: 'Reference 1: Phone', type: 'phone', required: true, page: 3, x: 204.5, yTop: 388.34, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref1_email', label: 'Reference 1: Email', type: 'email', required: true, page: 3, x: 204.5, yTop: 406.1, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref1_relationship', label: 'Reference 1: Relationship / how long known', type: 'text', required: true, page: 3, x: 204.5, yTop: 431.2, size: 9, maxWidth: 331.5, height: 12 },
      { key: 'ref2_full_name', label: 'Reference 2: Full name', type: 'text', required: true, page: 3, x: 204.5, yTop: 461.66, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref2_phone', label: 'Reference 2: Phone', type: 'phone', required: true, page: 3, x: 204.5, yTop: 479.42, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref2_email', label: 'Reference 2: Email', type: 'email', required: true, page: 3, x: 204.5, yTop: 497.18, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref2_relationship', label: 'Reference 2: Relationship / how long known', type: 'text', required: true, page: 3, x: 204.5, yTop: 518.8, size: 9, maxWidth: 330.9, height: 12 },
      { key: 'ref3_full_name', label: 'Reference 3: Full name', type: 'text', page: 3, x: 204.5, yTop: 552.74, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref3_phone', label: 'Reference 3: Phone', type: 'phone', page: 3, x: 204.5, yTop: 570.5, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref3_email', label: 'Reference 3: Email', type: 'email', page: 3, x: 204.5, yTop: 588.26, size: 9, maxWidth: 250, height: 12 },
      { key: 'ref3_relationship', label: 'Reference 3: Relationship / how long known', type: 'text', page: 3, x: 204.5, yTop: 612.4, size: 9, maxWidth: 330, height: 12 },
      { key: 'applicant_signature', label: 'Applicant signature', type: 'signature', required: true, page: 4, x: 172, yTop: 359.3, width: 184, height: 15.5 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 4, x: 411, yTop: 356.8, size: 9, maxWidth: 125.6, height: 12 },
      { key: 'printed_name', label: 'Printed name', type: 'text', required: true, page: 4, x: 171, yTop: 376, size: 9, maxWidth: 185.2, height: 12 },
    ],
  },

  {
    key: 'handbook_acknowledgement',
    title: 'Care Pro Handbook Acknowledgement',
    sortOrder: 2,
    file: `${BASE_PATH}/Care Pro Handbook Acknowledge.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Care Pro printed name', type: 'text', page: 0, x: 62, yTop: 594.5, size: 9, maxWidth: 350, height: 18.3 },
      { key: 'printed_name_date', label: 'Date', type: 'date', page: 0, x: 460, yTop: 593.5, size: 9, maxWidth: 100, height: 12 },
      { key: 'signature', label: 'Care Pro signature', type: 'signature', page: 0, x: 62, yTop: 636.6, width: 350, height: 20 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 0, x: 460, yTop: 637, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'client_rights',
    title: 'Client Rights for Care Pros',
    sortOrder: 3,
    file: `${BASE_PATH}/Client Rights for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 0, x: 75, yTop: 701.2, width: 260, height: 20 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 0, x: 403.8, yTop: 700.6, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'hiretech_flow_chart',
    title: 'HIRETech Flow Chart',
    sortOrder: 4,
    file: `${BASE_PATH}/HIRETech Flow Chart - Lewis & Leigh, Inc. (18178).pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 0, x: 165, yTop: 691, width: 210, height: 20 },
      { key: 'reference_number', label: 'Reference number', type: 'text', required: true, page: 0, x: 478.6, yTop: 689.8, size: 9, maxWidth: 65, height: 12 },
    ],
  },

  {
    key: 'liquidated_damages',
    title: 'Liquidated Damages Payroll Deduction',
    sortOrder: 5,
    file: `${BASE_PATH}/Liquidated Damages Payroll Deduction form for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Employee printed name', type: 'text', page: 0, x: 62, yTop: 417.8, size: 9, maxWidth: 135, height: 12 },
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', page: 0, x: 366.4, yTop: 419.1, width: 150, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 0, x: 63.9, yTop: 524.4, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'prohibited_services',
    title: 'Prohibited Services Acknowledgement',
    sortOrder: 6,
    file: `${BASE_PATH}/Prohibited Services for Care Pros 08092026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', page: 0, x: 48, yTop: 595.8, width: 250, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 0, x: 373.3, yTop: 596.4, size: 9, maxWidth: 160, height: 12 },
    ],
  },

  {
    key: 'scope_of_services_acknowledgement',
    title: 'Scope of Services Acknowledgement',
    sortOrder: 7,
    file: `${BASE_PATH}/Scope of Services Acknowledgement for Care Pros 08052026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Employee printed name', type: 'text', page: 0, x: 76, yTop: 346.9, size: 9, maxWidth: 205, height: 12 },
      { key: 'printed_name_date', label: 'Date', type: 'date', page: 0, x: 402.5, yTop: 346.3, size: 9, maxWidth: 130, height: 12 },
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', page: 0, x: 77.3, yTop: 428.2, width: 205, height: 18 },
      { key: 'instructor_signature', label: 'Instructor signature', type: 'signature', staffOnly: true, page: 0, x: 76, yTop: 575.1, width: 205, height: 18 },
      { key: 'instructor_date', label: 'Instructor date', type: 'date', staffOnly: true, page: 0, x: 400, yTop: 575.1, size: 9, maxWidth: 130, height: 12 },
    ],
  },

  {
    key: 'driver_status',
    title: 'Driver Status',
    sortOrder: 8,
    file: `${BASE_PATH}/Driver Status for Care Pros 08082026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'driver_eligibility', label: 'Driver eligibility', type: 'radio', required: true, page: 0, options: [
        { value: 'eligible', label: 'Eligible Driver', x: 57, yTop: 404.97 },
        { value: 'not_eligible', label: 'Not Currently Eligible or Will Not Transport Clients', x: 57, yTop: 430.65 },
      ]},
      { key: 'printed_name', label: 'Care Pro printed name', type: 'text', page: 0, x: 62.6, yTop: 570.9, size: 9, maxWidth: 350, height: 12 },
      { key: 'printed_name_date', label: 'Date', type: 'date', page: 0, x: 450.6, yTop: 569.6, size: 9, maxWidth: 100, height: 12 },
      { key: 'signature', label: 'Care Pro signature', type: 'signature', page: 0, x: 63.2, yTop: 608.7, width: 350, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 0, x: 451.3, yTop: 607.4, size: 9, maxWidth: 100, height: 12 },
      { key: 'representative_signature', label: 'Company representative signature', type: 'signature', staffOnly: true, page: 0, x: 62, yTop: 647.3, width: 350, height: 18 },
      { key: 'representative_date', label: 'Date', type: 'date', staffOnly: true, page: 0, x: 451.3, yTop: 646.6, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'virtual_orientation',
    title: 'Virtual Orientation & Zoom Meeting Requirements',
    sortOrder: 9,
    file: `${BASE_PATH}/Care Pro Virtual Orientation and Zoom Meeting Requirements 07292026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Care Pro printed name', type: 'text', required: true, page: 0, x: 61.4, yTop: 652.6, size: 9, maxWidth: 350, height: 12 },
      { key: 'printed_name_date', label: 'Date', type: 'date', required: true, page: 0, x: 452.5, yTop: 652.6, size: 9, maxWidth: 100, height: 12 },
      { key: 'signature', label: 'Care Pro signature', type: 'signature', required: true, page: 0, x: 62, yTop: 690, width: 350, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 0, x: 451.9, yTop: 689.4, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'employment_agreement',
    title: 'Care Professional Employment Agreement',
    sortOrder: 10,
    file: `${BASE_PATH}/Care Professional Employment Agreement 07312026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_name', label: 'Employee name', type: 'text', page: 0, x: 159.4, yTop: 159.6, size: 9, maxWidth: 205, height: 12 },
      { key: 'effective_date', label: 'Effective date', type: 'date', shared: 'today_date', page: 0, format: 'dayOfMonthYear',
        daySlot: { x: 457.4, yTop: 159, maxWidth: 19.9 },
        monthSlot: { x: 75, yTop: 172.2, maxWidth: 65 },
        yearSlot: { x: 165, yTop: 172.2, maxWidth: 15 },
      },
      { key: 'employee_name_2', label: 'Employee name', type: 'text', required: true, page: 4, x: 393, yTop: 240.8, size: 9, maxWidth: 145, height: 12 },
      { key: 'employee_date', label: 'Date', type: 'date', required: true, page: 4, x: 357, yTop: 278.7, size: 9, maxWidth: 175, height: 12 },
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 4, x: 380, yTop: 316.6, width: 155, height: 16 },
      { key: 'employer_name', label: 'Employer name', type: 'text', staffOnly: true, page: 4, x: 394, yTop: 405.2, size: 9, maxWidth: 145, height: 12 },
      { key: 'employer_date', label: 'Date', type: 'date', staffOnly: true, page: 4, x: 357, yTop: 443.1, size: 9, maxWidth: 175, height: 12 },
      { key: 'employer_signature', label: 'Employer signature', type: 'signature', staffOnly: true, page: 4, x: 380, yTop: 481, width: 155, height: 16 },
    ],
  },

  {
    key: 'drug_alcohol_policy',
    title: 'Drug and Alcohol Policy',
    sortOrder: 12,
    file: `${BASE_PATH}/Drug and Alcohol Policy for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Printed name', type: 'text', required: true, page: 2, x: 148, yTop: 599.3, size: 9, maxWidth: 380, height: 12 },
      { key: 'applicant_signature', label: 'Applicant signature (if not yet offered employment)', type: 'signature', required: true, page: 2, x: 333, yTop: 626.9, width: 200, height: 18 },
      { key: 'employee_signature', label: 'Employee signature (if already employed)', type: 'signature', page: 2, x: 286, yTop: 654.5, width: 245, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 2, x: 104, yTop: 682.1, size: 9, maxWidth: 430, height: 12 },
    ],
  },

  {
    key: 'electronic_signature_consent',
    title: 'Electronic Signature Consent',
    sortOrder: 13,
    file: `${BASE_PATH}/Electronic Signatures for Care Pros 07292026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Care Pro printed name', type: 'text', required: true, page: 0, x: 62, yTop: 498.9, size: 9, maxWidth: 350, height: 12 },
      { key: 'printed_name_date', label: 'Date', type: 'date', required: true, page: 0, x: 450.6, yTop: 497.6, size: 9, maxWidth: 100, height: 12 },
      { key: 'signature', label: 'Care Pro signature', type: 'signature', required: true, page: 0, x: 62, yTop: 541.8, width: 350, height: 18 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 0, x: 450, yTop: 539.9, size: 9, maxWidth: 100, height: 12 },
    ],
  },

  {
    key: 'job_description_pca',
    title: 'Job Description — Personal Care Attendant',
    sortOrder: 15,
    file: `${BASE_PATH}/Job Description - Personal Care Attendant 08092026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 3, x: 48, yTop: 196.5, width: 195, height: 16 },
      { key: 'employee_date', label: 'Date', type: 'date', required: true, page: 3, x: 368, yTop: 196.5, size: 9, maxWidth: 95, height: 12 },
      { key: 'supervisor_signature', label: 'Supervisor signature', type: 'signature', staffOnly: true, page: 3, x: 48, yTop: 249.4, width: 195, height: 16 },
      { key: 'supervisor_date', label: 'Date', type: 'date', staffOnly: true, page: 3, x: 368, yTop: 249.4, size: 9, maxWidth: 95, height: 12 },
    ],
  },

  {
    key: 'mandated_reporting',
    title: 'Mandated Reporting of Abuse',
    sortOrder: 16,
    file: `${BASE_PATH}/Mandated Reporting of Abuse for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 2, x: 74.8, yTop: 95.9, width: 205, height: 16 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 2, x: 399.6, yTop: 94.7, size: 9, maxWidth: 120, height: 12 },
    ],
  },

  {
    key: 'medical_terminology',
    title: 'Medical Terminology',
    sortOrder: 17,
    file: `${BASE_PATH}/Medical Terminology for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', required: true, page: 2, x: 185.8, yTop: 220.3, width: 225, height: 16 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 2, x: 449, yTop: 220.3, size: 9, maxWidth: 80, height: 12 },
    ],
  },

  {
    key: 'osha_bloodborne_pathogens',
    title: 'OSHA Bloodborne Pathogens Standards',
    sortOrder: 18,
    file: `${BASE_PATH}/OSHA Bloodborne Pathogens Standards for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'employee_signature', label: 'Employee signature', type: 'signature', page: 2, x: 38.8, yTop: 628.3, width: 235, height: 16 },
      { key: 'signature_date', label: 'Date', type: 'date', page: 2, x: 362.8, yTop: 628.3, size: 9, maxWidth: 150, height: 13.3 },
    ],
  },

  {
    key: 'pc_scope_of_services',
    title: 'PC Scope of Services',
    sortOrder: 19,
    file: `${BASE_PATH}/PC Scope of Services for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Care Pro printed name', type: 'text', page: 4, x: 35.4, yTop: 408.4, size: 9, maxWidth: 250, height: 15.8 },
      { key: 'printed_name_date', label: 'Date', type: 'date', page: 4, x: 360, yTop: 407.7, size: 9, maxWidth: 115, height: 13.9 },
      { key: 'signature', label: 'Care Pro signature', type: 'signature', page: 4, x: 34.8, yTop: 487.8, width: 250, height: 16 },
      { key: 'instructor_signature', label: 'Instructor signature', type: 'signature', staffOnly: true, page: 4, x: 36, yTop: 607.7, width: 265, height: 17.3 },
      { key: 'instructor_date', label: 'Date', type: 'date', staffOnly: true, page: 4, x: 360, yTop: 607, size: 9, maxWidth: 130, height: 15.1 },
    ],
  },

  {
    key: 'sworn_disclosure_statement',
    title: 'Sworn Disclosure Statement',
    sortOrder: 20,
    file: `${BASE_PATH}/Sworn Disclosure Statement for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'applicant_name', label: 'Applicant name (print)', type: 'text', required: true, page: 0, x: 74.8, yTop: 239.9, size: 9, maxWidth: 335, height: 12 },
      { key: 'conviction_history', label: 'Ever convicted of, or pending felony/misdemeanor charges', type: 'radio', page: 0, options: [
        { value: 'no', label: 'No', x: 339.5, yTop: 284.9 },
        { value: 'yes', label: 'Yes', x: 452.2, yTop: 284.9 },
      ]},
      { key: 'conviction_explain', label: 'If yes, please explain', type: 'textarea', page: 0, x: 72.9, yTop: 331.8, size: 9, maxWidth: 460, lines: 4, lineHeight: 17 },
      { key: 'ssn_1', label: 'Social Security # (first 3)', type: 'text', required: true, page: 0, x: 227.8, yTop: 433.2, size: 9, maxWidth: 42, height: 12 },
      { key: 'ssn_2', label: 'Social Security # (middle 2)', type: 'text', required: true, page: 0, x: 287, yTop: 433.2, size: 9, maxWidth: 38, height: 12 },
      { key: 'ssn_3', label: 'Social Security # (last 4)', type: 'text', required: true, page: 0, x: 340.6, yTop: 433.8, size: 9, maxWidth: 38, height: 12 },
      { key: 'applicant_signature', label: 'Applicant signature', type: 'signature', required: true, page: 0, x: 178.4, yTop: 508.5, width: 225, height: 16 },
      { key: 'applicant_signature_date', label: 'Date', type: 'date', required: true, page: 0, x: 462.4, yTop: 507.9, size: 9, maxWidth: 70, height: 12 },
      { key: 'staff_signature', label: 'Staff signature', type: 'signature', staffOnly: true, page: 0, x: 154, yTop: 559.9, width: 250, height: 16 },
      { key: 'staff_signature_date', label: 'Date', type: 'date', staffOnly: true, page: 0, x: 463, yTop: 559.9, size: 9, maxWidth: 70, height: 12 },
      { key: 'fcra_name', label: 'Name (FCRA authorization)', type: 'text', required: true, page: 1, x: 205, yTop: 510.7, size: 9, maxWidth: 80, height: 12 },
      { key: 'fcra_signature', label: 'Applicant signature', type: 'signature', required: true, page: 1, x: 76, yTop: 603.5, width: 210, height: 16 },
      { key: 'fcra_date', label: 'Date', type: 'date', required: true, page: 1, x: 327, yTop: 603.5, size: 9, maxWidth: 140, height: 12 },
      { key: 'full_name', label: 'Full name (Last, First, Middle)', type: 'text', required: true, page: 2, x: 115, yTop: 99.7, size: 9, maxWidth: 385, height: 12 },
      { key: 'maiden_name', label: 'Maiden/previous name(s)', type: 'text', page: 2, x: 214, yTop: 174.6, size: 9, maxWidth: 300, height: 12 },
      { key: 'home_address_line1', label: 'Home address', type: 'text', page: 2, x: 160, yTop: 224.5, size: 9, maxWidth: 345, height: 12 },
      { key: 'home_address_city', label: 'City', type: 'text', page: 2, x: 141, yTop: 274.6, size: 9, maxWidth: 200, height: 12 },
      { key: 'home_address_state', label: 'State', type: 'text', page: 2, x: 365, yTop: 274.6, size: 9, maxWidth: 30, height: 12 },
      { key: 'home_address_zip', label: 'Zip code', type: 'text', page: 2, x: 427, yTop: 274.6, size: 9, maxWidth: 55, height: 12 },
      { key: 'release_ssn', label: 'Social Security Number', type: 'text', page: 2, x: 206, yTop: 349.4, size: 9, maxWidth: 105, height: 12 },
      // Free-text, not a native date picker — on mobile, the picker's year
      // wheel defaults to today and has to be scrolled back decade after
      // decade to reach a birth year, which is exactly what caused an
      // applicant's birthdate to be silently submitted with today's year
      // instead of the one she actually typed. Typing MM/DD/YYYY sidesteps
      // that entirely (same fix already used for date_of_birth in
      // documentTemplates.js).
      { key: 'release_dob', label: 'Date of birth', type: 'text', placeholder: 'MM/DD/YYYY', page: 2, x: 403, yTop: 349.4, size: 9, maxWidth: 120, height: 12 },
      { key: 'drivers_license_number', label: 'Driver\'s license number', type: 'text', page: 2, x: 211, yTop: 399.4, size: 9, maxWidth: 95, height: 12 },
      { key: 'drivers_license_state', label: 'State issuing', type: 'text', page: 2, x: 402, yTop: 399.4, size: 9, maxWidth: 125, height: 12 },
      { key: 'release_signature', label: 'Signature', type: 'signature', required: true, page: 3, x: 76, yTop: 223.2, width: 210, height: 16 },
      { key: 'release_date', label: 'Date', type: 'date', page: 3, x: 364, yTop: 223.2, size: 9, maxWidth: 160, height: 12 },
      { key: 'authority_signature', label: 'Applicant signature', type: 'signature', required: true, page: 4, x: 328, yTop: 492.1, width: 190, height: 16 },
    ],
  },

  {
    key: 'tb_risk_assessment',
    title: 'TB Screening and Risk Assessment',
    sortOrder: 21,
    file: `${BASE_PATH}/TB Risk Assessment Form for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'tb_symptoms', label: 'First screen for TB symptoms', type: 'checkbox_group', page: 0, options: [
        { value: 'none', label: 'None', x: 176.1, yTop: 234.4 },
        { value: 'cough', label: 'Cough', x: 57.9, yTop: 254.6 },
        { value: 'hemoptysis', label: 'Hemoptysis', x: 104.3, yTop: 254.3 },
        { value: 'fever', label: 'Fever', x: 266.4, yTop: 254.9 },
        { value: 'weight_loss', label: 'Weight loss', x: 310.8, yTop: 254.9 },
        { value: 'poor_appetite', label: 'Poor appetite', x: 380.1, yTop: 254.9 },
        { value: 'night_sweats', label: 'Night sweats', x: 459.1, yTop: 254.9 },
        { value: 'fatigue', label: 'Fatigue', x: 532.7, yTop: 254.9 },
      ]},
      { key: 'tb_risk_factors', label: 'Risk factor boxes', type: 'checkbox_group', page: 0, options: [
        { value: 'elevated_tb_country', label: 'Birth, travel, or residence in a country with an elevated TB rate', x: 64.7, yTop: 377.3 },
        { value: 'medical_conditions', label: 'Medical conditions increasing risk for progression to TB disease', x: 66.7, yTop: 486.9 },
        { value: 'immunosuppression', label: 'Immunosuppression, current or planned', x: 63.5, yTop: 548.4 },
        { value: 'close_contact', label: 'Close contact to someone with infectious TB disease', x: 64.7, yTop: 620.7 },
        { value: 'none', label: 'None; no TB testing indicated at this time', x: 64.1, yTop: 655.9 },
      ]},
      { key: 'patient_name', label: 'Patient name', type: 'text', required: true, page: 0, x: 95, yTop: 693.5, size: 9, maxWidth: 110, height: 12 },
      // See release_dob above — free-text so mobile's date-picker year wheel
      // (which defaults to today, not a birth year) can't silently submit
      // the wrong year.
      { key: 'dob', label: 'Date of birth', type: 'text', placeholder: 'MM/DD/YYYY', required: true, page: 0, x: 351, yTop: 693.5, size: 9, maxWidth: 95, height: 12 },
      { key: 'completed_by_name', label: 'Name of person completing assessment', type: 'text', staffOnly: true, page: 0, x: 192, yTop: 715.5, size: 8, maxWidth: 95, height: 12 },
      { key: 'completed_by_signature', label: 'Signature of person completing assessment', type: 'signature', staffOnly: true, page: 0, x: 464, yTop: 715.5, width: 100, height: 14 },
      { key: 'completed_by_title', label: 'Title/credentials of person completing assessment', type: 'text', staffOnly: true, page: 0, x: 227, yTop: 738.1, size: 8, maxWidth: 100, height: 12 },
      { key: 'assessment_date', label: 'Assessment date', type: 'date', staffOnly: true, page: 0, x: 410, yTop: 737.6, size: 9, maxWidth: 85, height: 12 },
    ],
  },

  {
    key: 'workplace_health_safety',
    title: 'Workplace Health & Safety Policy',
    sortOrder: 24,
    file: `${BASE_PATH}/Work Place Health & Safety Policy for Care Pros 07272026.pdf`,
    pageHeight: 792,
    fields: [
      { key: 'printed_name', label: 'Printed name', type: 'text', required: true, page: 1, x: 196.6, yTop: 287.9, size: 9, maxWidth: 165, height: 12 },
      { key: 'signature', label: 'Signature', type: 'signature', required: true, page: 1, x: 175.6, yTop: 332.4, width: 185, height: 16 },
      { key: 'signature_date', label: 'Date', type: 'date', required: true, page: 1, x: 103.6, yTop: 374.5, size: 9, maxWidth: 260, height: 12 },
      { key: 'initial', label: 'initial', type: 'signature', required: true, page: 0, x: 324.4, yTop: 492.5, width: 57.5, height: 13.8 },
      { key: 'initial2', label: 'initial2', type: 'signature', required: true, page: 0, x: 460, yTop: 551.9, width: 58.1, height: 15 },
      { key: 'initial3', label: 'initial3', type: 'signature', required: true, page: 0, x: 431.9, yTop: 617.6, width: 83.8, height: 13.1 },
      { key: 'initial4', label: 'initial4', type: 'signature', required: true, page: 0, x: 246.3, yTop: 704.4, width: 55.6, height: 14.4 },
    ],
  },

  {
    key: 'zoom_meeting_requirements',
    title: 'Zoom Meeting Requirements',
    sortOrder: 25,
    file: `${BASE_PATH}/Zoom Requirements - Copy 1.pdf`,
    pageHeight: 792,
    fields: [],
  },
];

export const getCareProTemplates = () =>
  [...CARE_PRO_TEMPLATES].sort((a, b) => a.sortOrder - b.sortOrder);
